import { forwardRef } from 'react';
import type {
  FontValue,
  TextStyleValue
} from '@elfsight/configurator-contracts';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { getInvertedColor } from '@elfsight/widget-utils';
import type { ButtonBaseProps } from './ButtonBase';
import { ButtonBase } from './ButtonBase';
import { computeBorderWidth } from './computeBorderWidth';
import { COLOR_BLACK, COLOR_TRANSPARENT, COLOR_WHITE } from './constants';

const FULL_WIDTH_STYLE = {
  width: '100%',
  horizontalPadding: 8
};

export type ButtonMode = 'contain' | 'outline';

export type ButtonProps = Omit<ButtonBaseProps, 'fontFamily'> & {
  text?: string;
  mode?: ButtonMode;
  fullWidth?: boolean;
  font?: TextStyleValue;
  fontFamily?: FontValue;
  horizontalPadding?: number;
  verticalPadding?: number;
  overlayOpacityOnHover?: number;
  overlayOpacityOnClick?: number;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      text,
      mode = 'contain',
      color = COLOR_BLACK,
      font = {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: 13
      },
      fontFamily,
      fontColor = COLOR_WHITE,
      borderWidth,
      borderColor,
      fullWidth = false,
      horizontalPadding,
      verticalPadding,
      overlayOpacityOnHover = 0.1,
      overlayOpacityOnClick = 0.1,
      variants,
      overlayVariants,
      style,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const { fontSize, fontWeight, fontStyle } = font;

    const isContain = mode === 'contain';
    const isOutline = mode === 'outline';

    const currentFontColor = isContain ? fontColor : color;
    const currentBorderWidth = borderWidth ?? computeBorderWidth(font);

    const hoverBorderColor = isOutline
      ? borderColor || color
      : getColorWithAlpha(currentFontColor, overlayOpacityOnHover);
    const hoverBackgroundColor = getColorWithAlpha(
      currentFontColor,
      overlayOpacityOnHover
    );

    const padding: ButtonBaseProps['padding'] =
      typeof verticalPadding === 'number' &&
      typeof horizontalPadding === 'number'
        ? [verticalPadding, horizontalPadding]
        : undefined;

    return (
      <ButtonBase
        {...forwardingProps}
        ref={forwardingRef}
        color={color}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontStyle={fontStyle}
        fontColor={currentFontColor}
        fontFamily={fontFamily?.value}
        borderWidth={currentBorderWidth}
        borderColor={borderColor}
        padding={padding}
        style={{
          ...(fullWidth ? { minWidth: FULL_WIDTH_STYLE.width } : {}),
          ...style
        }}
        variants={{
          ...variants,
          initial: {
            backgroundColor: isContain ? color : COLOR_TRANSPARENT,
            borderColor: isOutline ? borderColor || color : COLOR_TRANSPARENT,
            ...variants?.initial
          },
          hover: {
            borderColor: hoverBorderColor,
            ...variants?.hover
          },
          focus: {
            borderColor: hoverBorderColor,
            ...variants?.focus
          },
          focusVisible: {
            borderColor: hoverBorderColor,
            ...variants?.hover,
            ...variants?.focusVisible
          },
          tap: {
            borderColor: isOutline
              ? borderColor || color
              : getColorWithAlpha(
                  getInvertedColor(currentFontColor),
                  overlayOpacityOnClick
                ),
            ...variants?.tap
          }
        }}
        overlayVariants={{
          ...overlayVariants,
          initial: {
            backgroundColor: COLOR_TRANSPARENT,
            ...overlayVariants?.initial
          },
          hover: {
            backgroundColor: hoverBackgroundColor,
            ...overlayVariants?.hover
          },
          focus: {
            backgroundColor: hoverBackgroundColor,
            ...overlayVariants?.focus
          },
          focusVisible: {
            backgroundColor: hoverBackgroundColor,
            ...overlayVariants?.hover,
            ...overlayVariants?.focusVisible
          },
          tap: {
            backgroundColor: getColorWithAlpha(
              getInvertedColor(currentFontColor),
              overlayOpacityOnClick
            ),
            ...overlayVariants?.tap
          }
        }}
      >
        {text || children}
      </ButtonBase>
    );
  }
);
